import { useState, useEffect, useMemo, useCallback } from "react";
import { BranchesMetrics } from "../components/dashboard/branches-metrics";
import { OverallRating } from "../components/dashboard/overall-rating";
import { DateFilter } from "../components/messages/date-filter";
import { EmployeesMetrics } from "../components/dashboard/employees-metrics";
import { ChartMetrics } from "../components/dashboard/chart-metrics";
import { DashboardScopeFilter } from "../components/dashboard/dashboard-scope-filter";
import { CloverConnectionModal } from "../components/modals/CloverConnectionModal";
import { CloverCbuModal } from "../components/modals/CloverCbuModal";
import { CloverService, PendingCbuCloverBranch } from "../services/clover-service";
import { BranchService, EmployeeIssue } from "../services/branch-service";
import { UserService } from "../services/user-service";
import { EmployeeValidationWarningModal } from "../components/branches/employee-validation-warning-modal";
import { useToast } from "../context/toast-context";
import { PaycloudBranch } from "../types";
import { getLastMonth } from "../utils/get-last-month";
import { BranchStatus } from "../constants/branch-status";
import { isBranchReadyForCloverCbu } from "../utils/helpers";
import { useTranslation } from "react-i18next";

export function Dashboard() {
  const { startDate: defaultStartDate, endDate: defaultEndDate } =
    getLastMonth();

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [branches, setBranches] = useState<{ branchId: number; name: string }[]>([]);
  const [employees, setEmployees] = useState<{ userId: number; name: string }[]>([]);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [pendingBranches, setPendingBranches] = useState<PaycloudBranch[]>([]);
  const [showCbuModal, setShowCbuModal] = useState(false);
  const [pendingCbuBranches, setPendingCbuBranches] = useState<PendingCbuCloverBranch[]>([]);
  const [showEmployeeWarningModal, setShowEmployeeWarningModal] = useState(false);
  const [employeesWithIssues, setEmployeesWithIssues] = useState<EmployeeIssue[]>([]);
  
  const cloverService = useMemo(() => new CloverService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const userService = useMemo(() => new UserService(), []);
  const { showToast } = useToast();
  const { t } = useTranslation();

  const fetchPendingCbuBranches = useCallback(async () => {
    try {
      await branchService.getBranches(
        { limit: 100, offset: 0 },
        async (branchesData) => {
          const incompleteIds = new Set(
            branchesData.branches
              .filter((branch) => Number(branch.status) === BranchStatus.INCOMPLETE)
              .map((branch) => branch.branchId)
          );

          await cloverService.getPendingCbuBranches(
            { limit: 100, offset: 0 },
            (data) => {
              const readyBranches = data.branches.filter(
                (branch) =>
                  isBranchReadyForCloverCbu(branch.status) &&
                  !incompleteIds.has(branch.branchId)
              );
              setPendingCbuBranches(readyBranches);
              setShowCbuModal(readyBranches.length > 0);
            },
            (errorMessage: string) => {
              showToast(errorMessage, "error");
              setShowCbuModal(false);
            }
          );
        },
        () => {
          setShowCbuModal(false);
        }
      );
    } catch {
      setShowCbuModal(false);
    }
  }, [branchService, cloverService, showToast]);

  const fetchPendingBranches = useCallback(async () => {
    try {
      await cloverService.getPendingBranches(
        { limit: 100, offset: 0 },
        (data) => {
          setPendingBranches(data.branches);
          setShowBranchModal(data.branches.length > 0);
        },
        (errorMessage: string) => {
          showToast(errorMessage, "error");
          setShowBranchModal(false);
        }
      );
    } catch {
      setShowBranchModal(false);
    }
  }, [cloverService, showToast]);

  useEffect(() => {
    fetchPendingCbuBranches();
  }, [fetchPendingCbuBranches]);

  useEffect(() => {
    if (!showCbuModal) {
      fetchPendingBranches();
    }
  }, [fetchPendingBranches, showCbuModal]);

  useEffect(() => {
    branchService.getBranches(
      { limit: 200, offset: 0 },
      (data) => {
        setBranches(
          (data.branches || []).map((branch) => ({
            branchId: branch.branchId,
            name: branch.fantasyName || branch.name,
          }))
        );
      },
      (error) => showToast(error, "error")
    );
  }, [branchService, showToast]);

  useEffect(() => {
    userService.getEmployees(
      {
        limit: 500,
        offset: 0,
        ...(selectedBranch != null ? { branchid: selectedBranch } : {}),
      },
      (data) => {
        const unique = new Map<number, { userId: number; name: string }>();
        for (const employee of data.users || []) {
          if (!unique.has(employee.userId)) {
            unique.set(employee.userId, {
              userId: employee.userId,
              name: employee.name,
            });
          }
        }
        setEmployees(Array.from(unique.values()));
      },
      (error) => showToast(error, "error")
    );
  }, [userService, showToast, selectedBranch]);

  useEffect(() => {
    const validateAllEmployees = async () => {
      try {
        await branchService.getBranches(
          { limit: 100, offset: 0 },
          async (branchesData) => {
            const allEmployeesWithIssues: EmployeeIssue[] = [];
            
            const validationPromises = branchesData.branches.map(
              (branch) =>
                new Promise<void>((resolve) => {
                  branchService.validateEmployees(
                    branch.branchId,
                    (data) => {
                      if (!data.valid && data.employeesWithIssues) {
                        allEmployeesWithIssues.push(...data.employeesWithIssues);
                      }
                      resolve();
                    },
                    (error) => {
                      console.error(`Error validando sucursal ${branch.branchId}:`, error);
                      resolve();
                    }
                  );
                })
            );

            await Promise.all(validationPromises);

            if (allEmployeesWithIssues.length > 0) {
              setEmployeesWithIssues(allEmployeesWithIssues);
              setShowEmployeeWarningModal(true);
            }
          },
          (error) => {
            console.error("Error obteniendo sucursales:", error);
          }
        );
      } catch (error) {
        console.error("Error validando empleados:", error);
      }
    };

    validateAllEmployees();
  }, [branchService]);

  const validateDate = (date: string | null, fallback: string) => {
    return date || fallback;
  };

  const handleCbuModalClose = () => {
    setShowCbuModal(false);
  };

  const handleCbuSaved = async () => {
    await fetchPendingCbuBranches();
  };

  const showCbuCartel =
    showCbuModal &&
    pendingCbuBranches.some((branch) => isBranchReadyForCloverCbu(branch.status));

  const resetScopeFilters = () => {
    setSelectedBranch(null);
    setSelectedEmployee(null);
  };

  return (
    <main className="h-full flex overflow-hidden">
      <div className="flex w-full gap-2">
        <div className="flex flex-col flex-grow gap-6 max-w-[70%]">
          <div className="w-full h-24 rounded-lg bg-primary text-white">
            <OverallRating
              startDate={validateDate(startDate, defaultStartDate)}
              endDate={validateDate(endDate, defaultEndDate)}
              branchId={selectedBranch}
              employeeId={selectedEmployee}
            />
          </div>

          <div className="flex-grow bg-white text-black rounded-lg">
            <ChartMetrics
              startDate={validateDate(startDate, defaultStartDate)}
              endDate={validateDate(endDate, defaultEndDate)}
              branchId={selectedBranch}
              employeeId={selectedEmployee}
            />
          </div>

          <section className="h-48 bg-white rounded-lg overflow-x-auto custom-scrollbar-light">
            <div className="flex justify-start  h-full mr-2">
              <BranchesMetrics
                startDate={validateDate(startDate, defaultStartDate)}
                endDate={validateDate(endDate, defaultEndDate)}
                branchId={selectedBranch}
                employeeId={selectedEmployee}
              />
            </div>
          </section>
        </div>

        <div className="w-[30%]  flex flex-col gap-6">
          <div className="bg-white text-black rounded-lg">
            <div className="p-3 flex flex-col gap-3 h-full">
              <DateFilter
                size="sm"
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
              <DashboardScopeFilter
                branches={branches}
                employees={employees}
                selectedBranch={selectedBranch}
                selectedEmployee={selectedEmployee}
                setSelectedBranch={setSelectedBranch}
                setSelectedEmployee={setSelectedEmployee}
              />
              {(selectedBranch != null || selectedEmployee != null) && (
                <button
                  type="button"
                  onClick={resetScopeFilters}
                  className="text-xs text-[#EF0BB8] self-start hover:underline"
                >
                  {t("messages.filter.clean")}
                </button>
              )}
            </div>
          </div>

          <div className="flex-grow bg-[#a0abba] text-white rounded-lg overflow-y-auto custom-scrollbar-light">
            <EmployeesMetrics
              startDate={validateDate(startDate, defaultStartDate)}
              endDate={validateDate(endDate, defaultEndDate)}
              branchId={selectedBranch}
              employeeId={selectedEmployee}
            />
          </div>
        </div>
      </div>

      {showCbuCartel && (
        <CloverCbuModal
          isOpen={showCbuCartel}
          onClose={handleCbuModalClose}
          branches={pendingCbuBranches}
          onSaved={handleCbuSaved}
        />
      )}

      {showBranchModal && !showCbuCartel && (
        <CloverConnectionModal
          isOpen={showBranchModal}
          onClose={() => setShowBranchModal(false)}
          branches={pendingBranches}
          onBranchReassigned={fetchPendingBranches}
        />
      )}

      {showEmployeeWarningModal && (
        <EmployeeValidationWarningModal
          isOpen={showEmployeeWarningModal}
          employees={employeesWithIssues}
          onClose={() => setShowEmployeeWarningModal(false)}
        />
      )}
    </main>
  );
}
